import React, { useEffect, useState, useCallback } from 'react';
import {
  MapPin,
  Phone,
  X,
  Plus,
  Minus,
  Edit,
  ArrowLeft,
  Notebook,
  Bike,
  ShoppingBag,
  Save,
  AlertCircle,
  PlusIcon,
  CheckCircle2,
  IndianRupee,
  Truck,
  Calendar,
  XCircle,
} from 'lucide-react';
import { useNavigate, useParams } from 'react-router-dom';
import { format, addDays, isSameDay, parse, isAfter } from 'date-fns';
import { toast } from 'react-toastify';
import axiosInstance from '../../utils/axiosInstance';
import { useUser } from '../../contexts/UserContext';

// Assuming these components are defined elsewhere in your project
// If not, you would need to implement them
import Loader from '@/components/common/Loader';
import { Button } from '@/components/ui/button';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { Card, CardTitle, CardHeader, CardContent } from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';
import { cn } from '@/lib/utils';
import { AddItemsModal } from '../../pages/vendor/AddItemsModal';
import BottomNav from '@/components/layouts/Footer/BottomNav';

const timeSlots = ['10:00-12:00', '12:00-14:00', '14:00-16:00', '16:00-18:00', '18:00-20:00'];

const formatTimeSlot = (slot) => {
  const [start, end] = slot.split('-');
  return `${formatTime(start)} - ${formatTime(end)}`;
};

const formatTime = (time) => {
  const [hours, minutes] = time.split(':');
  const date = new Date(0, 0, 0, Number.parseInt(hours), Number.parseInt(minutes));
  return date.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
};

export default function OrderDetails() {
  const [isAllSlotsOccupiedForToday, setIsAllSlotsOccupiedForToday] = useState(false);
  const [isCancelModalOpen, setIsCancelModalOpen] = useState(false);
  const [isRescheduleModalOpen, setIsRescheduleModalOpen] = useState(false);
  const [cancelReason, setCancelReason] = useState('');
  const [rescheduleDate, setRescheduleDate] = useState(new Date());
  const [selectedTimeSlot, setSelectedTimeSlot] = useState('');
  const navigate = useNavigate();
  const params = useParams();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(false);
  const [editedItems, setEditedItems] = useState([]);
  const [tempOrder, setTempOrder] = useState(null);
  const [isDeliveryModalOpen, setIsDeliveryModalOpen] = useState(false);
  const [delivery, setDelivery] = useState(0);
  const { user } = useUser();
  // New state for edit mode
  const [isEditMode, setIsEditMode] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const [removedItems, setRemovedItems] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const fetchOrderDetails = useCallback(async () => {
    const { id } = params;

    try {
      setLoading(true);
      const { data } = await axiosInstance.get(
        `${import.meta.env.VITE_API_URL}/rest/subziwale/api/v1/order/details?orderId=${id}`,
        {
          headers: {
            sessionToken: localStorage.getItem('sessionToken'),
            refreshToken: localStorage.getItem('refreshToken'),
          },
        }
      );

      setDelivery(data?.deliveryCharges);
      setTempOrder(data);

      // Initialize items with proper default values for updatedVariant and updatedNetPrice
      setEditedItems(
        data.items.map((item) => ({
          ...item,
          edited: false,
          updatedVariant: item.variant || 0,
          updatedNetPrice: item.netPrice || 0,
        }))
      );

      setRemovedItems([]);
    } catch (error) {
      console.error(error);
      toast.error('Failed to load order details');
    } finally {
      setLoading(false);
    }
  }, [params]);

  useEffect(() => {
    fetchOrderDetails();
  }, [fetchOrderDetails]);

  useEffect(() => {
    if (tempOrder) {
      setOrder(tempOrder);
      setTempOrder(null);
    }
  }, [tempOrder]);

  // Check for changes in edited items
  useEffect(() => {
    if (editedItems.some((item) => item.edited) || removedItems.length > 0) {
      setHasChanges(true);
    } else {
      setHasChanges(false);
    }
  }, [editedItems, removedItems]);

  const handleCancelOrder = async () => {
    if (!cancelReason) {
      toast.error('Please select a reason for cancellation');
      return;
    }

    try {
      setLoading(true);
      await axiosInstance.put(
        `${import.meta.env.VITE_API_URL}/rest/subziwale/api/v1/order/status?orderId=${params?.id}&status=Cancelled`,
        {
          cancellationReason: cancelReason,
        },
        {
          headers: {
            sessionToken: localStorage.getItem('sessionToken'),
            refreshToken: localStorage.getItem('refreshToken'),
          },
        }
      );
      toast.success('Order Cancelled Successfully');
      navigate('/vendor?tab=Cancelled');
    } catch (error) {
      console.error('Error cancelling order:', error);
      toast.error(error.message || 'Failed to cancel order! Please try again');
    } finally {
      setLoading(false);
      setIsCancelModalOpen(false);
    }
  };

  const handleRescheduleOrder = async () => {
    if (!selectedTimeSlot) {
      toast.error('Please select a time slot');
      return;
    }

    const data = {
      date: format(rescheduleDate, 'yyyy-MM-dd'),
      selectedTimeSlot,
    };

    if (new Date(data.date).getDate() == new Date().getDate()) {
      const currentHour = new Date().getHours();
      const selectedHour = parseInt(selectedTimeSlot.split('-')[0].split(':')[0]);

      if (currentHour >= selectedHour) {
        toast.error('Slot Not Available! Please Choose Next Available Slot');
        return;
      }
    }
    try {
      setLoading(true);
      await axiosInstance.put(
        `${import.meta.env.VITE_API_URL}/rest/subziwale/api/v1/order/status?orderId=${params?.id}&status=Rescheduled`,
        {
          rescheduleDeliveryDate: format(rescheduleDate, 'yyyy-MM-dd'),
          rescheduleTimeSlot: selectedTimeSlot,
        },
        {
          headers: {
            sessionToken: localStorage.getItem('sessionToken'),
            refreshToken: localStorage.getItem('refreshToken'),
          },
        }
      );

      toast.success('Order Rescheduled Successfully');
      navigate('/vendor?tab=Rescheduled');
    } catch (error) {
      console.error('Error rescheduling order:', error);
      toast.error('Failed to reschedule order. Please try again.');
    } finally {
      setLoading(false);
      setIsRescheduleModalOpen(false);
    }
  };

  const handleMarkDelivered = async (status) => {
    try {
      setLoading(true);
      await axiosInstance.put(
        `${import.meta.env.VITE_API_URL}/rest/subziwale/api/v1/order/status?orderId=${params?.id}&status=${status}`,
        {},
        {
          headers: {
            sessionToken: localStorage.getItem('sessionToken'),
            refreshToken: localStorage.getItem('refreshToken'),
          },
        }
      );
      if (status === 'Completed') {
        toast.success('Order Delivered Successfully');
        navigate('/vendor?tab=Completed');
      } else {
        toast.success('Payment Done');
        navigate('/vendor?tab=Paid');
      }
    } catch (error) {
      console.error('Error marking order as delivered:', error);
      toast.error('Failed to update order status! Please Try Again');
    } finally {
      setLoading(false);
    }
  };

  const handleQuantityChange = (index, change) => {
    const newItems = editedItems.map((item, i) => {
      if (i === index) {
        const newQuantity = Math.max(0, item.quantity + change);
        // Use updatedNetPrice if available, otherwise use netPrice
        const priceToUse =
          item.updatedNetPrice !== undefined ? item.updatedNetPrice : item.netPrice;
        const newTotal = newQuantity * priceToUse;

        return {
          ...item,
          quantity: newQuantity,
          total: newTotal,
          edited: true,
        };
      }
      return item;
    });

    setEditedItems(newItems);
    updateOrderSummary(newItems);
  };

  const updateOrderSummary = (items) => {
    // Calculate MRP total
    const mrpTotal = items.reduce((sum, item) => {
      return sum + item.mrp * item.quantity;
    }, 0);

    // Calculate net total using updatedNetPrice when available
    const netTotal = items.reduce((sum, item) => {
      const priceToUse = item.updatedNetPrice !== undefined ? item.updatedNetPrice : item.netPrice;
      return sum + priceToUse * item.quantity;
    }, 0);

    const discount = mrpTotal - netTotal;
    const total = netTotal + (order?.platformFees || 0) + (order?.deliveryCharges || 0);

    setOrder((prevOrder) => ({
      ...prevOrder,
      mrp: mrpTotal,
      discount: discount,
      total: total,
    }));
  };

  const handleSubmitChanges = async () => {
    try {
      setLoading(true);

      // Don't submit if there are no items
      if (editedItems.length === 0) {
        toast.error('Cannot submit an order with no items');
        return;
      }

      // Prepare items for submission by transferring updatedVariant and updatedNetPrice to variant and netPrice
      const itemsToSubmit = editedItems.map((item) => {
        if (item.edited) {
          return {
            ...item,
            variant: item.updatedVariant !== undefined ? item.updatedVariant : item.variant,
            netPrice: item.updatedNetPrice !== undefined ? item.updatedNetPrice : item.netPrice,
            // Keep the updatedVariant and updatedNetPrice for UI display
          };
        }
        return item;
      });

      const updatedOrderData = {
        ...order,
        total: order.total,
        items: itemsToSubmit,
        note: order?.note || '',
      };

      await axiosInstance.put(
        `${import.meta.env.VITE_API_URL}/rest/subziwale/api/v1/order?orderId=${params?.id}`,
        updatedOrderData,
        {
          headers: {
            sessionToken: localStorage.getItem('sessionToken'),
            refreshToken: localStorage.getItem('refreshToken'),
          },
        }
      );

      toast.success('Order updated successfully');
      setIsEditMode(false);
      fetchOrderDetails();
    } catch (error) {
      console.error('Error updating order:', error);
      toast.error('Failed to update order! Please try again');
    } finally {
      setLoading(false);
    }
  };

  // Cancel editing without saving changes
  const handleCancelEdit = () => {
    // Reset to original order data
    fetchOrderDetails();
    setIsEditMode(false);
  };

  const isSlotOccupied = (slot) => {
    const [start] = slot.split('-');
    const slotTime = parse(start, 'HH:mm', new Date());
    const currentTime = new Date();

    if (isSameDay(rescheduleDate, new Date())) {
      return isAfter(currentTime, slotTime);
    }
    return false;
  };

  const handleAddDeliveryCharge = async (deliveryAmount) => {
    if (order?.status !== 'Pending' && order?.status !== 'Rescheduled') {
      toast.info('Order is Already Delivered!');
      return;
    }

    let updatedDelivery;
    let mrp;
    let totalAmount;

    if (order?.deliveryCharges < deliveryAmount) {
      updatedDelivery = deliveryAmount;
      mrp = order?.mrp;
      totalAmount = order?.total + (Number(deliveryAmount) - order?.deliveryCharges);
    } else {
      updatedDelivery = deliveryAmount;
      mrp = order?.mrp;
      totalAmount = order?.total - (order?.deliveryCharges - Number(deliveryAmount));
    }

    const updatedOrder = {
      ...order,
      deliveryCharges: Number(updatedDelivery),
      mrp,
      total: totalAmount,
    };

    try {
      setLoading(true);
      await axiosInstance.put(
        `${import.meta.env.VITE_API_URL}/rest/subziwale/api/v1/order?orderId=${params?.id}`,
        updatedOrder,
        {
          headers: {
            sessionToken: localStorage.getItem('sessionToken'),
            refreshToken: localStorage.getItem('refreshToken'),
          },
        }
      );

      fetchOrderDetails();
      toast.success('Delivery charge updated successfully');
    } catch (error) {
      console.error(error);
      toast.error('Failed to update delivery charge');
    } finally {
      setLoading(false);
      setIsDeliveryModalOpen(false);
    }
  };

  useEffect(() => {
    const allOccupied = timeSlots.every(isSlotOccupied);
    setIsAllSlotsOccupiedForToday(allOccupied);
  }, [rescheduleDate]);

  const cancelReasons = [
    'Incorrect address',
    'Customer not responding',
    'Denied delivery',
    'Unavailable',
    'Other',
  ];

  const handleWeightChange = (e, index) => {
    const newVariant = e.target.value;

    // If the input is empty, just return without making changes
    if (!newVariant.trim()) {
      return;
    }

    const product = editedItems[index];

    // Validate that the new value is numeric
    if (isNaN(parseFloat(newVariant))) {
      toast.error('Please enter a valid number');
      return;
    }

    // Find the base price per unit - use the original variant and netPrice for calculation
    const basePrice = product.netPrice / parseFloat(product.variant);

    // Calculate new price based on the new variant
    const newPrice = basePrice * parseFloat(newVariant);

    const updatedItems = editedItems.map((item, i) => {
      if (i === index) {
        return {
          ...item,
          edited: true,
          updatedVariant: newVariant,
          updatedNetPrice: newPrice, // Update the net price based on the new variant
          total: newPrice * item.quantity, // Update the total price
        };
      } else {
        return item;
      }
    });

    setEditedItems(updatedItems);
    updateOrderSummary(updatedItems);
  };

  // Check if order can be modified
  const canModifyOrder =
    order &&
    order.status !== 'Cancelled' &&
    order.status !== 'Completed' &&
    order.status !== 'Paid';

  // Helper function to get the correct price display
  const getItemPrice = (item) => {
    return item.updatedNetPrice !== undefined ? item.updatedNetPrice : item.netPrice;
  };

  // Helper function to get the correct variant display
  const getItemVariant = (item) => {
    return item.updatedVariant !== undefined ? item.updatedVariant : item.variant;
  };

  return (
    <div className="bg-gray-50 min-h-screen">
      <header className="sticky top-0 z-10 bg-white px-4 py-3 shadow-sm">
        <div className="flex items-center gap-4">
          <button onClick={() => navigate(-1)} className="focus:outline-none">
            <ArrowLeft className="h-6 w-6 font-normal cursor-pointer" />
          </button>
          <h1 className="text-lg font-medium">Order Details</h1>

          {/* Edit mode indicator */}
          {isEditMode && (
            <Badge
              variant="outline"
              className="ml-auto bg-amber-50 text-amber-700 border-amber-200"
            >
              Edit Mode
            </Badge>
          )}
        </div>
      </header>

      {!tempOrder && loading && (
        <div className="fixed top-0 max-w-md h-full w-full flex items-center justify-center">
          <Loader />
        </div>
      )}

      {(order || tempOrder) && (
        <main className="mx-auto border-l pb-16 border-r max-w-md space-y-4 p-4">
          <Card>
            <CardContent className="p-4">
              <div className="flex justify-between items-center mb-4">
                <h2 className="font-medium text-lg">Order Items</h2>
                {/* Modify Order button - only show when not in edit mode and order can be modified */}
                {canModifyOrder && !isEditMode && (
                  <Button
                    onClick={() => setIsEditMode(true)}
                    variant="outline"
                    className="flex items-center gap-1 border-green-600 text-green-600 hover:bg-green-50"
                  >
                    <Edit className="h-4 w-4" />
                    Edit Items
                  </Button>
                )}
                {canModifyOrder && isEditMode && (
                  <Button
                    onClick={() => setIsModalOpen(true)}
                    variant="outline"
                    className="flex items-center gap-1 border-green-600 text-green-600 hover:bg-green-50"
                  >
                    <PlusIcon className="h-4 w-4" />
                    Add Items
                  </Button>
                )}
              </div>

              {/* Edit mode instructions */}
              {isEditMode && (
                <div className="mb-4 p-3 bg-blue-50 rounded-md text-sm text-blue-700 flex items-start gap-2">
                  <AlertCircle className="h-5 w-5 shrink-0 mt-0.5" />
                  <div>
                    <p className="font-medium">Edit Mode Active</p>
                    <p>
                      You can adjust quantities, change weights, Add new item or remove items. Click
                      Save when done.
                    </p>
                  </div>
                </div>
              )}

              <div className="space-y-4">
                {editedItems.map((item, index) => (
                  <div
                    key={item.inventoryId + '-' + index}
                    className={cn(
                      'flex items-center gap-3 px-3 rounded-lg transition-colors',
                      isEditMode && 'bg-gray-50 py-2 border',
                      item.quantity === 0 && 'opacity-60'
                    )}
                  >
                    <img
                      loading="lazy"
                      src={item.productImageUrl || '/placeholder.svg'}
                      alt={item.productName}
                      className="rounded-md w-16 h-16 object-cover"
                    />
                    <div className="flex-1">
                      <h3 className="font-medium text-sm">{item.productName}</h3>
                      {item.edited && (
                        <Badge
                          variant="outline"
                          className="mt-1 bg-green-50 text-green-600 border-green-200 text-xs"
                        >
                          Modified
                        </Badge>
                      )}
                      {item.quantity === 0 && (
                        <Badge
                          variant="outline"
                          className="mt-1 bg-red-50 text-red-600 border-red-200 text-xs"
                        >
                          Removed
                        </Badge>
                      )}
                    </div>

                    {isEditMode ? (
                      <div className="flex flex-col items-end gap-2">
                        <div className="flex items-center gap-1 border rounded-md overflow-hidden">
                          <Button
                            size="icon"
                            variant="ghost"
                            className="h-8 w-8 rounded-none hover:bg-red-50 hover:text-red-600"
                            onClick={() => handleQuantityChange(index, -1)}
                          >
                            <Minus className="h-4 w-4" />
                          </Button>
                          <span className="text-sm font-medium w-7 text-center">
                            {item.quantity}
                          </span>
                          <Button
                            size="icon"
                            variant="ghost"
                            className="h-8 w-8 rounded-none hover:bg-green-50 hover:text-green-600"
                            onClick={() => handleQuantityChange(index, 1)}
                          >
                            <Plus className="h-4 w-4" />
                          </Button>
                        </div>

                        {item?.unit !== 'pc' && (
                          <div className="flex flex-col items-end gap-1">
                            <div className="flex items-center gap-1">
                              <span className="text-xs text-gray-500">
                                Current: {getItemVariant(item)} {item.unit}
                              </span>
                            </div>
                            <div className="flex items-center gap-1">
                              <input
                                type="text"
                                placeholder="New weight"
                                onChange={(e) => handleWeightChange(e, index)}
                                className="w-20 border rounded-md px-2 py-1 border-gray-200 focus:border-black focus:outline-none text-sm"
                              />
                              <span className="text-sm text-gray-500">{item.unit}</span>
                            </div>
                          </div>
                        )}

                        <p className="text-sm text-gray-700 font-medium">
                          ₹ {(getItemPrice(item) * item.quantity).toFixed(2)}
                        </p>
                      </div>
                    ) : (
                      <div className="text-right">
                        {item?.unit === 'pc' ? (
                          <p className="font-medium text-sm">
                            {item?.quantity} {item.unit}
                          </p>
                        ) : (
                          <p className="font-medium text-sm">
                            {item?.quantity} × {getItemVariant(item)} {item.unit}
                          </p>
                        )}
                        <p className="text-xs text-gray-500">
                          ₹ {(getItemPrice(item) * item.quantity).toFixed(2)}
                        </p>
                      </div>
                    )}
                  </div>
                ))}
              </div>

              {/* Edit mode action buttons */}
              {isEditMode && (
                <div className="mt-6 flex gap-2 justify-end">
                  <Button
                    variant="outline"
                    onClick={handleCancelEdit}
                    className="flex items-center gap-1"
                  >
                    <X className="h-4 w-4" />
                    Cancel
                  </Button>
                  <Button
                    onClick={handleSubmitChanges}
                    className="bg-green-600 hover:bg-green-700 flex items-center gap-1"
                    disabled={!hasChanges || loading}
                  >
                    <Save className="h-4 w-4" />
                    Save Changes
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-4">
              <div className="flex mb-3 items-center justify-between">
                <h2 className="font-medium">Order Summary</h2>
              </div>
              <div className="space-y-3 text-sm">
                <div className="flex justify-between">
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-gray-600 flex items-center gap-1">
                      <Notebook className="h-4 w-4 inline-block" /> Items total
                    </span>
                    {(order || tempOrder).discount > 0 && (
                      <Badge variant="outline" className="bg-blue-50 text-blue-600 border-blue-200">
                        Saved ₹{(order || tempOrder).discount.toFixed(2)}
                      </Badge>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-sm line-through text-gray-400">
                      ₹{(order || tempOrder).mrp?.toFixed(2)}
                    </span>
                    <span className="text-sm font-medium">
                      ₹{((order || tempOrder).mrp - (order || tempOrder).discount)?.toFixed(2)}
                    </span>
                  </div>
                </div>

                <div className="flex justify-between items-center">
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <span className="text-sm text-gray-600 flex items-center gap-1 cursor-pointer">
                          <Bike className="h-4 w-4 inline-block" /> Delivery charge
                          {canModifyOrder && (
                            <Edit
                              onClick={() => setIsDeliveryModalOpen(true)}
                              className="w-4 h-4 cursor-pointer text-green-600"
                            />
                          )}
                        </span>
                      </TooltipTrigger>
                      <TooltipContent>
                        <p>Click to edit delivery charge</p>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>

                  <span className="text-sm font-medium">
                    {(order || tempOrder).deliveryCharges > 0
                      ? `₹${(order || tempOrder).deliveryCharges?.toFixed(2)}`
                      : 'FREE'}
                  </span>
                </div>

                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-600 flex items-center gap-1">
                    <ShoppingBag className="h-4 w-4 inline-block" />
                    Platform Fees
                  </span>
                  <span className="text-sm font-medium">
                    ₹
                    {(order || tempOrder).platformFees !== 0
                      ? (order || tempOrder).platformFees?.toFixed(2)
                      : 0}
                  </span>
                </div>

                <Separator className="my-2" />

                <div className="flex justify-between items-center pt-1">
                  <span className="text-base font-medium">Bill total</span>
                  <span className="text-base font-medium">
                    ₹{(order || tempOrder).total?.toFixed(2)}
                  </span>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Rest of the component remains the same */}
          <Card>
            <CardContent className="p-4">
              <h2 className="mb-4 font-medium">Delivery Details</h2>
              <div className="space-y-3">
                <div className="flex gap-3">
                  <MapPin className="h-5 w-5 shrink-0 text-gray-900" />
                  <p className="text-sm text-gray-600">
                    {(order || tempOrder)?.deliveryAddress || 'Not Provided'}
                  </p>
                </div>
                <div className="flex gap-3">
                  <Phone
                    className="h-5 w-5 shrink-0 cursor-pointer text-gray-900"
                    onClick={() => {
                      window.open(`tel:${(order || tempOrder)?.contactNo}`);
                    }}
                  />
                  <p className="text-sm text-gray-600">
                    {(order || tempOrder)?.contactNo || 'Not Provided'}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-4">
              <h2 className="mb-4 font-medium">Order Status</h2>
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <span className="text-sm text-gray-600">Status:</span>
                  <Badge
                    className={cn(
                      'font-medium',
                      (order || tempOrder).status === 'Completed' &&
                        'bg-green-100 text-green-700 hover:bg-green-100',
                      (order || tempOrder).status === 'Cancelled' &&
                        'bg-red-100 text-red-700 hover:bg-red-100',
                      (order || tempOrder).status === 'Pending' &&
                        'bg-blue-100 text-blue-700 hover:bg-blue-100',
                      (order || tempOrder).status === 'Rescheduled' &&
                        'bg-amber-100 text-amber-700 hover:bg-amber-100',
                      (order || tempOrder).status === 'Paid' &&
                        'bg-purple-100 text-purple-700 hover:bg-purple-100'
                    )}
                  >
                    {(order || tempOrder).status === 'Completed'
                      ? 'Delivered'
                      : (order || tempOrder).status}
                  </Badge>
                  {(order || tempOrder)?.status === 'Completed' && (
                    <Badge variant="outline" className="bg-red-50 text-red-600 border-red-200">
                      Payment Due
                    </Badge>
                  )}
                </div>
                <p className="text-sm text-gray-600">
                  Date:{' '}
                  <span className="font-medium">
                    {format(new Date((order || tempOrder).deliveryDate), 'PPP')}
                  </span>
                </p>
                <p className="text-sm text-gray-600">
                  Time Slot:{' '}
                  <span className="font-medium">
                    {!(order || tempOrder)?.expressDelivery
                      ? (order || tempOrder)?.timeSlot
                        ? formatTimeSlot((order || tempOrder)?.timeSlot)
                        : 'Not Given'
                      : (order || tempOrder)?.timeSlot}
                  </span>
                </p>
                {(order || tempOrder)?.note && (
                  <p className="text-sm text-gray-600">
                    Note: <span className="font-medium">{(order || tempOrder)?.note}</span>
                  </p>
                )}
                <p className="text-sm text-gray-600">
                  Delivery Type:{' '}
                  <span className="font-medium">
                    {(order || tempOrder).selfPickup
                      ? 'Self Pickup'
                      : (order || tempOrder).expressDelivery
                        ? 'Express Delivery'
                        : 'Standard Delivery'}
                  </span>
                </p>
              </div>
            </CardContent>
          </Card>

          {/* Action buttons */}
          {true && (
            <Card className="mb-14 shadow-sm border-t-4 border-t-green-500">
              <CardContent className="p-4">
                <div className="grid grid-cols-2 gap-2">
                  {!loading &&
                    (order || tempOrder)?.status !== 'Completed' &&
                    (order || tempOrder)?.status !== 'Cancelled' &&
                    (order || tempOrder)?.status !== 'Paid' && (
                      <Button
                        className="bg-green-600 hover:bg-green-700 text-white"
                        onClick={() => handleMarkDelivered('Completed')}
                      >
                        <Truck className="h-4 w-4 mr-2" />
                        Deliver
                      </Button>
                    )}
                  {(order || tempOrder)?.status === 'Completed' && (
                    <Button
                      className="bg-green-600 hover:bg-green-700 text-white col-span-2"
                      onClick={() => handleMarkDelivered('Paid')}
                    >
                      <IndianRupee className="h-4 w-4 mr-2" />
                      Complete Payment
                    </Button>
                  )}
                  {!loading &&
                    (order || tempOrder)?.status !== 'Completed' &&
                    (order || tempOrder)?.status !== 'Cancelled' &&
                    (order || tempOrder)?.status !== 'Paid' && (
                      <Button
                        className="bg-purple-600 hover:bg-purple-700 text-white"
                        onClick={() => handleMarkDelivered('Paid')}
                      >
                        <CheckCircle2 className="h-4 w-4 mr-2" />
                        Mark as Paid
                      </Button>
                    )}
                  {((order || tempOrder)?.status === 'Pending' ||
                    (order || tempOrder)?.status === 'Rescheduled') && (
                    <>
                      <Button
                        className="bg-red-600 hover:bg-red-700 text-white"
                        onClick={() => setIsCancelModalOpen(true)}
                      >
                        <XCircle className="h-4 w-4 mr-2" />
                        Cancel Order
                      </Button>
                      <Button
                        className="bg-blue-600 hover:bg-blue-700 text-white"
                        onClick={() => setIsRescheduleModalOpen(true)}
                      >
                        <Calendar className="h-4 w-4 mr-2" />
                        Reschedule
                      </Button>
                    </>
                  )}
                </div>
              </CardContent>
            </Card>
          )}
        </main>
      )}

      <BottomNav page="orders" />

      {/* Delivery Charges Modal */}
      {isDeliveryModalOpen && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 max-w-md mx-auto">
          <Card className="w-80 mx-4">
            <CardContent className="p-6 relative">
              <Button
                variant="ghost"
                size="icon"
                className="absolute top-2 right-2 text-gray-500 hover:text-red-500"
                onClick={() => setIsDeliveryModalOpen(false)}
              >
                <X className="h-5 w-5" />
              </Button>

              <h3 className="text-lg font-medium mb-4 text-center">Delivery Charges</h3>

              <div className="flex items-center justify-center gap-4 my-6">
                <Button
                  variant="outline"
                  size="icon"
                  onClick={() => setDelivery(Math.max(0, Number(delivery) - 1))}
                >
                  <Minus className="h-4 w-4" />
                </Button>

                <Input
                  type="number"
                  value={delivery}
                  onChange={(e) => setDelivery(e.target.value)}
                  className="w-20 text-center"
                />

                <Button
                  variant="outline"
                  size="icon"
                  onClick={() => setDelivery(Number(delivery) + 1)}
                >
                  <Plus className="h-4 w-4" />
                </Button>
              </div>

              <div className="flex justify-end gap-2 mt-4">
                <Button variant="outline" onClick={() => setIsDeliveryModalOpen(false)}>
                  Cancel
                </Button>
                <Button
                  className="bg-green-600 hover:bg-green-700 text-white"
                  onClick={() => handleAddDeliveryCharge(delivery)}
                  disabled={loading}
                >
                  Update
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Cancel Order Modal */}
      {isCancelModalOpen && (
        <div className="fixed inset-0 max-w-md mx-auto bg-black/50 flex items-center justify-center p-4 z-50">
          <Card className="w-full max-w-md">
            <CardContent className="p-6">
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-lg font-medium">Cancel Order</h2>
                <Button variant="ghost" size="icon" onClick={() => setIsCancelModalOpen(false)}>
                  <X className="h-5 w-5" />
                </Button>
              </div>

              <p className="mb-4 text-sm text-gray-600">
                Please select a reason for cancelling this order:
              </p>

              <RadioGroup
                onValueChange={setCancelReason}
                value={cancelReason}
                className="space-y-2"
              >
                {cancelReasons.map((reason) => (
                  <div
                    key={reason}
                    className="flex items-center space-x-2 border rounded-md p-2 hover:bg-gray-50"
                  >
                    <RadioGroupItem value={reason} id={reason} />
                    <Label htmlFor={reason} className="cursor-pointer flex-1">
                      {reason}
                    </Label>
                  </div>
                ))}
              </RadioGroup>

              <div className="mt-6 flex justify-end gap-2">
                <Button variant="outline" onClick={() => setIsCancelModalOpen(false)}>
                  Close
                </Button>
                <Button
                  className="bg-red-600 hover:bg-red-500 text-white"
                  onClick={handleCancelOrder}
                  disabled={!cancelReason || loading}
                >
                  Cancel Order
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Reschedule Order Modal */}
      {isRescheduleModalOpen && (
        <div className="fixed inset-0 z-50 max-w-md mx-auto bg-black/50 flex items-center justify-center p-4">
          <Card className="w-full max-w-md">
            <CardHeader className="pb-2">
              <CardTitle>Reschedule Order</CardTitle>
            </CardHeader>
            <CardContent className="p-6">
              <Button
                variant="ghost"
                size="icon"
                className="absolute top-4 right-4"
                onClick={() => setIsRescheduleModalOpen(false)}
              >
                <X className="h-5 w-5" />
              </Button>

              <p className="mb-4 text-sm text-gray-600">Please select a new date for your order:</p>

              <div className="flex justify-between mb-4 gap-2">
                <Button
                  className={cn(
                    'flex-1 transition-colors',
                    isSameDay(rescheduleDate, new Date())
                      ? 'bg-green-600 hover:bg-green-600 text-white'
                      : 'bg-white text-black border border-gray-300 hover:bg-gray-50'
                  )}
                  onClick={() => setRescheduleDate(new Date())}
                  disabled={isAllSlotsOccupiedForToday}
                >
                  Today
                </Button>
                <Button
                  className={cn(
                    'flex-1 transition-colors',
                    isSameDay(rescheduleDate, addDays(new Date(), 1))
                      ? 'bg-green-600 hover:bg-green-600 text-white'
                      : 'bg-white text-black border border-gray-300 hover:bg-gray-50'
                  )}
                  onClick={() => setRescheduleDate(addDays(new Date(), 1))}
                >
                  {format(addDays(new Date(), 1), 'EEE, MMM d')}
                </Button>
                <Button
                  className={cn(
                    'flex-1 transition-colors',
                    isSameDay(rescheduleDate, addDays(new Date(), 2))
                      ? 'bg-green-600 hover:bg-green-600 text-white'
                      : 'bg-white text-black border border-gray-300 hover:bg-gray-50'
                  )}
                  onClick={() => setRescheduleDate(addDays(new Date(), 2))}
                >
                  {format(addDays(new Date(), 2), 'EEE, MMM d')}
                </Button>
              </div>

              <div className="mt-4">
                <label htmlFor="timeSlot" className="block text-sm font-medium text-gray-700 mb-2">
                  Select Time Slot
                </label>
                <Select onValueChange={setSelectedTimeSlot} value={selectedTimeSlot}>
                  <SelectTrigger id="timeSlot" className="w-full">
                    <SelectValue placeholder="Select a time slot" />
                  </SelectTrigger>
                  <SelectContent>
                    {timeSlots.map((slot) => (
                      <SelectItem key={slot} value={slot} disabled={isSlotOccupied(slot)}>
                        {formatTimeSlot(slot)}
                        {isSlotOccupied(slot) && ' (Unavailable)'}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="mt-6 flex justify-end gap-2">
                <Button variant="outline" onClick={() => setIsRescheduleModalOpen(false)}>
                  Close
                </Button>
                <Button
                  className="bg-green-600 hover:bg-green-500 text-white"
                  onClick={handleRescheduleOrder}
                  disabled={!selectedTimeSlot || loading}
                >
                  {loading ? 'Rescheduling...' : 'Reschedule'}
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {isModalOpen && (
        <AddItemsModal
          isOpen={isModalOpen}
          onClose={() => setIsModalOpen(false)}
          onAddItem={(newItem) => {
            // Check if the item already exists in editedItems
            const existingItemIndex = editedItems.findIndex(
              (item) =>
                item.productId === newItem.productId && item.inventoryId === newItem.inventoryId
            );

            if (existingItemIndex >= 0) {
              // If item exists, update its quantity
              const updatedItems = [...editedItems];
              updatedItems[existingItemIndex].quantity += newItem.quantity;
              updatedItems[existingItemIndex].total =
                updatedItems[existingItemIndex].quantity * updatedItems[existingItemIndex].netPrice;
              updatedItems[existingItemIndex].edited = true;
              setEditedItems(updatedItems);
            } else {
              // If item doesn't exist, add it to the list with proper initialization
              setEditedItems([
                ...editedItems,
                {
                  ...newItem,
                  updatedVariant: newItem.variant,
                  updatedNetPrice: newItem.netPrice,
                },
              ]);
            }

            // Update order summary
            updateOrderSummary([...editedItems, ...(existingItemIndex >= 0 ? [] : [newItem])]);

            // Set hasChanges to true since we've modified the order
            setHasChanges(true);
          }}
        />
      )}
    </div>
  );
}
